import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popup } from '../popover/Popup';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import {
  mcFilter2Filled,
  mcFilter2Outline,
  mcSearch,
} from '@mc/react-ui-icons';
import { IData } from '../virtual-data/VirtualData';
import { Divider } from '../divider/Divider';
import { Button } from '../button/Button';
import {
  Callout,
  DateInput,
  DateTimeInput,
  FieldValuesFilterData,
  FinalTableComputedColumn,
  HorizontalLoader,
  LabelValue,
  Layout,
  List,
  ListItem,
  NoData,
  NumberInput,
  Space,
  Spinner,
  StringInput,
  Tooltip,
} from '../index';
import classNames from 'classnames';
import {
  getColumnField,
  getColumnTitleAndValue,
  getIdOrValueFromRawValue,
  isColumnSupportsFulltext,
  isColumnSupportsRange,
} from './utils';
import { isFieldFilterFilled } from './fieldValuesFilterUtils';
import {
  DataResponseVO,
  FieldValuesFilterType,
  FieldValuesFilterVO,
} from '@mc/data-filter-react';
import { debounce } from 'lodash-es';
import { InputRange } from '../input-range/InputRange';
import { DataFieldValuesRequestVO } from '@mc/data-filter-react';

export type ColumnFilterProps<DATA extends IData<ID>, ID> = {
  column: FinalTableComputedColumn<DATA>;
  searchFieldValues: (
    fieldValuesRequest: DataFieldValuesRequestVO
  ) => Promise<DataResponseVO<DATA>>;
  fieldValuesFilter?: FieldValuesFilterData;
  setFieldValuesFilter: (fieldValuesFilter: FieldValuesFilterData) => void;
  fieldValuesFilterMaxFetchedItems: number;
  renderIfNotFilled?: boolean;
};

export const ColumnFilter = <DATA extends IData<ID>, ID extends unknown>({
  searchFieldValues,
  fieldValuesFilter,
  setFieldValuesFilter,
  fieldValuesFilterMaxFetchedItems,
  column,
  renderIfNotFilled = true,
}: ColumnFilterProps<DATA, ID>) => {
  const [isOpen, setOpen] = useState(false);

  const field = getColumnField(column);
  const data = useMemo(() => {
    let result = fieldValuesFilter?.[field] || {
      filterType: FieldValuesFilterType.VALUES,
      values: [],
    };
    return result;
  }, [field, fieldValuesFilter]);
  const isFilled = isFieldFilterFilled(data);

  if (!renderIfNotFilled && !isFilled) {
    return null;
  }

  return (
    <div
      onClick={(e) => {
        // Kvůli nested popup, kdyby zde nebylo, tak by se po zavření popup provedlo řazení, které je na onclick na nadřazeném divu
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Popup
        nested={true}
        open={isOpen}
        onOpen={() => {
          setOpen(true);
        }}
        // closeOnDocumentClick={false}
        // closeOnEscape={false}
        onClose={() => {
          setOpen(false);
        }}
        arrow
        mode={'edit'}
        trigger={
          <div
            className={classNames(Classes.FINAL_TABLE_HEADER_ICON_ACTION)}
            title={'Nastavit filtr'}
          >
            <Icon icon={isFilled ? mcFilter2Filled : mcFilter2Outline} />
          </div>
        }
      >
        {(onClose) => {
          return (
            <ColumnFilterForm<DATA, ID>
              fieldValuesFilterMaxFetchedItems={
                fieldValuesFilterMaxFetchedItems
              }
              searchFieldValues={searchFieldValues}
              column={column}
              data={data}
              setEmptyData={() => {
                const newFilter = {
                  ...fieldValuesFilter,
                };
                delete newFilter[field];
                setFieldValuesFilter(newFilter);
              }}
              setData={(data) => {
                const newFilter = {
                  ...fieldValuesFilter,
                  [field]: data,
                };
                setFieldValuesFilter(newFilter);
              }}
              onClose={() => {
                onClose();
              }}
            />
          );
        }}
      </Popup>
    </div>
  );
};

export type ColumnFilterFormProps<DATA extends IData<ID>, ID> = {
  column: FinalTableComputedColumn<DATA>;
  searchFieldValues: (
    fieldValuesRequest: DataFieldValuesRequestVO
  ) => Promise<DataResponseVO<DATA>>;
  data: FieldValuesFilterVO;
  setData: (data: FieldValuesFilterVO) => void;
  setEmptyData: () => void;
  onClose: () => void;
  fieldValuesFilterMaxFetchedItems: number;
};

export const ColumnFilterForm = <DATA extends IData<ID>, ID extends unknown>({
  column,
  data,
  searchFieldValues,
  onClose,
  fieldValuesFilterMaxFetchedItems,
  setData,
  setEmptyData,
}: ColumnFilterFormProps<DATA, ID>) => {
  const field = getColumnField(column);
  const [fulltext, setFulltext] = React.useState('');
  const [range, setRange] = React.useState<(string | undefined)[]>([
    undefined,
    undefined,
  ]);
  const [showFilter, setShowFilter] = React.useState(false);
  const [fulltextInputText, setFulltextInputText] = React.useState('');
  const [range1InputText, setRange1InputText] = React.useState<any>();
  const [range2InputText, setRange2InputText] = React.useState<any>();
  const [items, setItems] = useState<DATA[] | undefined>();
  const lastSearchRef = useRef<number>(0);
  const [searching, setSearching] = useState<boolean>(false);
  const [currentData, setCurrentData] = useState<FieldValuesFilterVO>(data);
  const initialFilled = useMemo(() => {
    return isFieldFilterFilled(data);
  }, []);

  const showRange = useMemo(() => {
    return isColumnSupportsRange(column.type);
  }, [column]);
  const showFulltext = useMemo(() => {
    return isColumnSupportsFulltext(column);
  }, [column]);

  const setNewItems = (newItems: DATA[]) => {
    // Při prvním načtení řešíme, zda zobrazit filtr
    if (items === undefined) {
      const existsMoreItems =
        newItems.length === fieldValuesFilterMaxFetchedItems + 1;

      // Pokud není vyplněn filtr a jsou načtena všechna data a je jich málo, není třeba zobrazovat filtr
      if (!existsMoreItems && newItems.length < 10) {
        setShowFilter(false);
      } else {
        setShowFilter(true);
      }
    }

    setItems(newItems || []);
  };

  // Fulltext search
  useEffect(() => {
    if (showFulltext) {
      if (column.type === 'boolean') {
        setNewItems([
          // @ts-ignore
          {
            // @ts-ignore
            [column.accessor]: true,
          },
          // @ts-ignore
          {
            // @ts-ignore
            [column.accessor]: false,
          },
        ]);
      } else {
        lastSearchRef.current++;
        const searchNumber = lastSearchRef.current;
        setSearching(true);
        searchFieldValues({ field, fulltext })
          .then((response) => {
            if (searchNumber === lastSearchRef.current) {
              setNewItems(response.rows || []);
              setSearching(false);
            }
          })
          .catch((_) => {
            if (searchNumber === lastSearchRef.current) {
              setNewItems([]);
              setSearching(false);
            }
          });
      }
    }
  }, [field, fulltext, showFulltext]);

  // Range search
  useEffect(() => {
    if (showRange) {
      lastSearchRef.current++;
      const searchNumber = lastSearchRef.current;
      setSearching(true);
      searchFieldValues({
        field,
        // @ts-ignore
        range,
      })
        .then((response) => {
          if (searchNumber === lastSearchRef.current) {
            setNewItems(response.rows || []);
            setSearching(false);
          }
        })
        .catch((_) => {
          if (searchNumber === lastSearchRef.current) {
            setNewItems([]);
            setSearching(false);
          }
        });
    }
  }, [field, range, showRange]);

  const handleZrusitFiltr = useCallback(() => {
    if (initialFilled) {
      // const emptyData: FieldValuesFilterVO = {
      //   filterType: FieldValuesFilterType.VALUES,
      //   values: [],
      // };
      setEmptyData();
    }
    onClose();
  }, [currentData]);

  const doFilter = useCallback(() => {
    if (isFieldFilterFilled(currentData)) {
      setData(currentData);
    } else {
      setEmptyData();
    }

    onClose();
  }, [currentData]);

  const debouncedFulltext = React.useCallback(
    debounce((text: any) => {
      setFulltext(text);
    }, 500),
    []
  );

  const searchByRange = () => {
    setRange([range1InputText, range2InputText]);
  };

  // ---

  if (items === undefined) {
    return (
      <div
        className={classNames(Classes.FINAL_TABLE_COLUMN_FILTER_FORM, {
          [Classes.FINAL_TABLE_COLUMN_FILTER_FORM_RANGE]: showRange,
        })}
      >
        <div className={Classes.FINAL_TABLE_COLUMN_FILTER_FORM_BODY}>
          <Spinner className={'p-2'} size={24} />
        </div>
      </div>
    );
  }

  const canCancelFilter =
    isFieldFilterFilled(currentData) || isFieldFilterFilled(data);
  let existsMoreItems = items?.length === fieldValuesFilterMaxFetchedItems + 1;

  // TODO
  // existsMoreItems = true;

  // ---
  let valuesFilter;
  if (showFilter) {
    switch (column.type) {
      case undefined:
      case 'text':
        valuesFilter = (
          <StringInput
            value={fulltextInputText}
            placeholder="Vyhledat"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFulltextInputText(e.target.value);
              debouncedFulltext(e.target.value);
            }}
            rightElement={<Icon fixedWidth icon={mcSearch} />}
          />
        );
        break;
      case 'integer':
      case 'decimal':
      case 'currency':
      case 'currencyDecimal':
        valuesFilter = (
          <LabelValue label={'Vyhledat hodnoty (od - do)'} lastItem>
            <div className={'d-flex'}>
              <InputRange>
                <NumberInput
                  onSearch={searchByRange}
                  value={range1InputText}
                  onChange={(value) => {
                    setRange1InputText(value);
                  }}
                />
                <NumberInput
                  onSearch={searchByRange}
                  value={range2InputText}
                  onChange={(value) => {
                    setRange2InputText(value);
                  }}
                />
              </InputRange>
              <Button
                dataTest={'search'}
                className={'ml-1'}
                onClick={searchByRange}
                style={'primary'}
                icon={<Icon fixedWidth icon={mcSearch} />}
              />
            </div>
          </LabelValue>
        );
        break;
      case 'date':
        valuesFilter = (
          <LabelValue label={'Vyhledat hodnoty (od - do)'} lastItem>
            <div className={'d-flex justify-content-between'}>
              <InputRange className={Classes.DATE_INPUT_RANGE}>
                <DateInput
                  // onSearch={searchByRange}
                  value={range1InputText}
                  onChange={(value) => {
                    setRange1InputText(value);
                  }}
                />
                <DateInput
                  // onSearch={searchByRange}
                  value={range2InputText}
                  onChange={(value) => {
                    setRange2InputText(value);
                  }}
                />
              </InputRange>
              <Button
                dataTest={'search'}
                onClick={searchByRange}
                style={'primary'}
                icon={<Icon fixedWidth icon={mcSearch} />}
              />
            </div>
          </LabelValue>
        );
        break;
      case 'dateTime':
        // Date time hledáme jako
        valuesFilter = (
          <LabelValue label={'Vyhledat hodnoty (od - do)'} lastItem>
            <div className={'d-flex'}>
              <InputRange>
                <DateTimeInput
                  // onSearch={searchByRange}
                  value={range1InputText}
                  onChange={(value) => {
                    setRange1InputText(value);
                  }}
                />
                <DateTimeInput
                  // onSearch={searchByRange}
                  value={range2InputText}
                  onChange={(value) => {
                    setRange2InputText(value);
                  }}
                />
              </InputRange>
              <Button
                dataTest={'search'}
                className={'ml-1'}
                onClick={searchByRange}
                style={'primary'}
                icon={<Icon fixedWidth icon={mcSearch} />}
              />
            </div>
          </LabelValue>
        );
        break;
      case 'boolean':
        break;
    }
  }

  const toggleCheck = (value: string) => {
    if (currentData.values?.includes(value)) {
      setCurrentData({
        ...currentData,
        values: currentData.values?.filter((v) => v !== value),
      });
    } else {
      setCurrentData({
        ...currentData,
        values: [...(currentData.values || []), value],
      });
    }
  };

  let header;
  if (existsMoreItems) {
    header = (
      <Space vertical>
        {valuesFilter}
        <Callout intent={'info'}>
          Zobrazeno prvních {fieldValuesFilterMaxFetchedItems} záznamů
        </Callout>
      </Space>
    );
  } else {
    header = valuesFilter;
  }

  const renderItems = items
    ? existsMoreItems
      ? items.slice(0, items.length - 1)
      : items
    : [];

  return (
    <div
      className={classNames(Classes.FINAL_TABLE_COLUMN_FILTER_FORM, {
        [Classes.FINAL_TABLE_COLUMN_FILTER_FORM_RANGE]: showRange,
      })}
    >
      <div className={Classes.FINAL_TABLE_COLUMN_FILTER_FORM_BODY}>
        <Layout h100 header={header}>
          <List fullHeight className={'mt-1'} mode={'input'} compact>
            {items !== undefined && searching && <HorizontalLoader hover />}
            {renderItems.length === 0 && <NoData />}
            {renderItems.map((rowData, index) => {
              let columnTitleAndValue = getColumnTitleAndValue(
                column,
                rowData,
                0,
                0
              );
              const rowId = getIdOrValueFromRawValue(
                columnTitleAndValue.rawValue
              );

              const checked = currentData.values?.includes(rowId);

              let listItem = (
                <ListItem
                  key={`${index}`}
                  rowCheck
                  checkable
                  checked={checked}
                  onCheck={() => {
                    toggleCheck(rowId);
                  }}
                  htmlTitle={
                    typeof columnTitleAndValue.title === 'string'
                      ? columnTitleAndValue.title
                      : undefined
                  }
                >
                  {columnTitleAndValue.cell}
                </ListItem>
              );

              if (
                !columnTitleAndValue.title ||
                typeof columnTitleAndValue.title === 'string'
              ) {
                return listItem;
              } else {
                return (
                  <Tooltip trigger={listItem}>
                    {columnTitleAndValue.title}
                  </Tooltip>
                );
              }
            })}
          </List>
        </Layout>
      </div>
      <Divider horizontal />
      <div className={Classes.FINAL_TABLE_COLUMN_FILTER_FORM_FOOTER}>
        <Button
          onClick={handleZrusitFiltr}
          disabled={!canCancelFilter}
          dataTest={'cancel'}
        >
          Zrušit filtr
        </Button>
        <Button onClick={doFilter} style={'primary'} dataTest={'ok'}>
          OK
        </Button>
      </div>
    </div>
  );
};
